import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MiniLeadForm from "@/components/MiniLeadForm";
import usePageSEO from "@/hooks/usePageSEO";

export default function Learn() {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);

  usePageSEO({
    title: "Learn — Financial education without the jargon",
    description: "Educational articles on life insurance, annuities, tax-advantaged wealth, and legacy planning from Lifespan Wealth Partners.",
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet("/learn/categories"),
      apiGet("/articles"),
    ]).then(([c, a]) => {
      if (cancelled) return;
      setCategories(c || []);
      setArticles(a || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const articlesByCategory = (cid) => articles.filter((a) => a.category_id === cid);

  return (
    <div data-testid="learn-index" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Learning hub</span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05] max-w-3xl">
            Financial education without the jargon.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            Plain-language articles on protection, accumulation, retirement income, and legacy. New pieces published regularly.
          </p>
        </section>

        {/* Newsletter signup */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-12">
          <MiniLeadForm
            source="newsletter"
            title="Send me the weekly Lifespan Letter"
            subtitle="One short, jargon-free email per week. Unsubscribe anytime."
            withName={false}
            testIdPrefix="newsletter"
          />
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          {categories.map((cat) => {
            const items = articlesByCategory(cat.id);
            if (!items.length) return null;
            return (
              <div key={cat.id} data-testid={`learn-category-${cat.id}`} className="mb-16">
                <div className="flex items-end justify-between gap-4 mb-6">
                  <div className="reveal">
                    <span className="lw-eyebrow">{cat.name}</span>
                    <p className="mt-2 text-sm sm:text-base text-lw-muted max-w-2xl">{cat.blurb}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/learn/${a.slug}`}
                      data-testid={`article-card-${a.slug}`}
                      className="lw-card p-6 flex flex-col h-full group"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(11,37,69,0.06), rgba(212,162,76,0.12))", color: "#0B2545" }}>
                          <BookOpen size={16} />
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-lw-navy/55 font-semibold flex items-center gap-1">
                          <Clock size={11} /> {a.read_time_min} min read
                        </span>
                      </div>
                      <h3 className="font-serif-display text-lg text-lw-navy leading-tight">{a.title}</h3>
                      <p className="mt-2 text-sm text-lw-muted leading-relaxed flex-1">{a.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-lw-navy group-hover:text-[#8a6a2b]">Read article <ArrowRight size={14} /></span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      </main>
      <Footer />
    </div>
  );
}
